import { Button, EmptyState } from "@community-project-org/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarX } from "lucide-react";
import { useMemo, useState } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import type { components } from "../../generated/api.js";
import { groupSlotsByDay } from "./slotDays.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type AppointmentSlotDTO = components["schemas"]["AppointmentSlotDTO"];
type BookingDTO = components["schemas"]["BookingDTO"];

/** Wie weit die Terminwahl vorausschaut. Zwei Wochen reichen für Praxis und Studio. */
const DAYS_AHEAD = 14;

/**
 * Terminwahl: erst der Tag, dann die Uhrzeit, dann buchen.
 *
 * Die Slots kommen fertig vom Server (Öffnungszeiten, Dauer, Puffer, Belegung); hier
 * wird nur gruppiert und angezeigt. Ob der Slot beim Absenden noch frei ist, sagt
 * erst die Antwort — bis dahin bleibt die Schaltfläche im Ladezustand.
 */
export function AppointmentPicker({ offer }: { offer: ServiceDTO }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = offer.venueTimeZone ?? "Europe/Berlin";

  const slots = useQuery({
    queryKey: ["offer-slots", offer.id],
    queryFn: () =>
      apiRequest<AppointmentSlotDTO[]>(`/offers/${encodeURIComponent(offer.id)}/slots?days=${DAYS_AHEAD}`, {
        tenant: null,
      }),
  });

  const days = useMemo(() => groupSlotsByDay(slots.data ?? [], timeZone), [slots.data, timeZone]);
  const [chosenDate, setChosenDate] = useState<string | null>(null);
  const [chosenSlot, setChosenSlot] = useState<string | null>(null);
  const day = days.find((entry) => entry.date === chosenDate) ?? days[0];

  const book = useMutation({
    mutationFn: (startsAt: string) =>
      apiRequest<BookingDTO>("/bookings", {
        method: "POST",
        body: { serviceId: offer.id, startsAt },
        tenant: null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      navigate("/tickets");
    },
    onError: async (error) => {
      // Vergeben: die Liste ist veraltet. Neu laden, damit der Slot verschwindet.
      if (error instanceof ApiRequestError && error.status === 409) {
        setChosenSlot(null);
        await slots.refetch();
      }
    },
  });

  if (slots.isPending) {
    return <p className="text-[14px] text-ink-3">Freie Termine werden geladen …</p>;
  }
  if (slots.isError) {
    return <p className="text-[14px] text-danger">Die Termine sind gerade nicht erreichbar.</p>;
  }
  if (!day) {
    return (
      <EmptyState
        icon={<CalendarX aria-hidden="true" />}
        title="Gerade kein Termin frei"
        description={`In den nächsten ${DAYS_AHEAD} Tagen ist alles belegt oder geschlossen.`}
      />
    );
  }

  return (
    <div className="grid gap-4">
      <div role="group" aria-label="Tag" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {days.map((entry) => (
          <Button
            key={entry.date}
            variant={entry.date === day.date ? "primary" : "secondary"}
            aria-pressed={entry.date === day.date}
            className="shrink-0"
            onClick={() => {
              setChosenDate(entry.date);
              setChosenSlot(null);
            }}
          >
            {entry.label}
          </Button>
        ))}
      </div>

      <div role="group" aria-label="Uhrzeit" className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {day.slots.map(({ slot, time }) => (
          <Button
            key={slot.startsAt}
            variant={chosenSlot === slot.startsAt ? "primary" : "secondary"}
            aria-pressed={chosenSlot === slot.startsAt}
            onClick={() => setChosenSlot(slot.startsAt)}
          >
            <span className="num">{time}</span>
          </Button>
        ))}
      </div>

      {book.error && (
        <p className="text-[13px] text-danger" role="alert">
          {book.error instanceof ApiRequestError ? book.error.message : "Das hat nicht geklappt."}
        </p>
      )}

      <Button
        disabled={!chosenSlot || book.isPending}
        aria-busy={book.isPending}
        onClick={() => chosenSlot && book.mutate(chosenSlot)}
      >
        {book.isPending ? "Wird gebucht …" : "Termin buchen"}
      </Button>
    </div>
  );
}
